import React from 'react';
import classNames from 'classnames';
import { Listing } from 'models/Listing';
import { getDealImage } from 'utils';
import useCallbackAsync from 'hooks/useCallbackAsync';
import api from 'api';
import { useDropzone } from 'react-dropzone';
import { Loader } from 'ncoded-component-library';
import ResolveImage from 'components/ResolveImage';
import {
  getImgUrl,
  RESIZED_WIDTH,
  ResizedWidth,
} from 'components/ResolveImage/utils';
import { DropzoneFile } from 'components/Dropzone/utils';
import UploadIcon from 'icons/Upload.icon';

import './ListingMainImageWithUploadOption.styles.scss';

type ListingMainImageWithUploadOptionProps = {
  className?: string;
  isDisabledUpload?: boolean;
  isRWC: boolean;
  listing: Partial<Listing>;
  width?: ResizedWidth;
  updateGallery: (dealId: string, gallery: DropzoneFile[]) => void;
};

const ListingMainImageWithUploadOption: React.FC<
  ListingMainImageWithUploadOptionProps
> = (props) => {
  const {
    className,
    isRWC,
    listing,
    width = RESIZED_WIDTH[480],
    isDisabledUpload = false,
    updateGallery,
  } = props;

  const classes = classNames(
    'listing-main-image-with-upload-option',
    { clickable: !isDisabledUpload },
    className,
  );

  const dealId = listing?.dealRoomId;
  const listingId = listing?._id;
  const imageSrc = listing?.gallery[0]?.url;

  const asCompanyLogo = !imageSrc && isRWC;

  const dealImage = getDealImage(imageSrc, isRWC);

  const [uploadListingFile, isLoading] = useCallbackAsync(
    async (files, _, config, customName) => {
      const file = files[0];

      if (!file) return;

      const { uploadedFile, listing } = await api.listings.uploadFile(
        file,
        'gallery',
        listingId,
        config,
        customName,
      );

      let gallery = listing?.gallery || [];

      if (gallery.length > 1) {
        gallery = [
          gallery.find(({ name }) => name === uploadedFile.name),
          ...gallery.filter(({ name }) => name !== uploadedFile.name),
        ];

        api.listings.reorderGallery(
          listingId,
          gallery.map(({ _id }) => _id),
        );
      }

      updateGallery(dealId, gallery);
    },
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    maxFiles: 1,
    disabled: isDisabledUpload,
    onDrop: uploadListingFile,
  });

  const { onClick, ...restOfRootProps } = getRootProps();

  return (
    <div
      className={classes}
      {...restOfRootProps}
      {...(!isDisabledUpload && {
        onClick: (e) => {
          onClick(e);
          e.stopPropagation();
        },
      })}
    >
      {isLoading && <Loader />}

      <input {...getInputProps()} />

      {!isDisabledUpload && (
        <div className="my-overlay">
          <UploadIcon />
        </div>
      )}

      <ResolveImage
        src={getImgUrl(dealImage, width)}
        alt="Listing"
        asCompanyLogo={asCompanyLogo}
      />
    </div>
  );
};

export default React.memo(ListingMainImageWithUploadOption);
